import { useEffect, useState, type ReactNode } from 'react';
import { Z_DEBUG } from '../../../features/quickstart/config/zIndexLayers';
import { DEBUG_SURFACE_EVENT_ISOLATION } from './debugEventIsolation';

export type GuestDebugSection = {
  id: string;
  label: string;
  content: ReactNode;
};

export type GuestDebugPanelProps = {
  sections: GuestDebugSection[];
  /** Header accent, e.g. the campaign / beacon primary colour. */
  accentColor?: string;
  /** Storage for the selected section; defaults to localStorage. */
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
};

export const GUEST_DEBUG_PANEL_OPEN_KEY = 'guest-debug-panel-open';
export const GUEST_DEBUG_PANEL_SECTION_KEY = 'guest-debug-panel-section';

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * GuestDebugPanel — the one debug surface on the guest player.
 *
 * A single tab, closed by default, that opens into a side panel hosting every debug
 * section (stream stats, beacon phase controls, player input inspector). It replaces the
 * three separately-positioned overlays that used to stack on top of each other and block
 * the page. Mount it only on a debug build; the caller owns that gate.
 */
export function GuestDebugPanel({ sections, accentColor = '#FACC15', storage = defaultStorage() }: GuestDebugPanelProps) {
  // Never restore an expanded panel over a fresh stream, even if the last visit left it open.
  const [open, setOpen] = useState(false);
  const [sectionId, setSectionId] = useState<string>(() => {
    try {
      const saved = storage?.getItem(GUEST_DEBUG_PANEL_SECTION_KEY);
      if (saved && sections.some((s) => s.id === saved)) return saved;
    } catch { /* Storage may be disabled in embedded/private browsers. */ }
    return sections[0]?.id ?? '';
  });

  useEffect(() => {
    try { storage?.setItem(GUEST_DEBUG_PANEL_OPEN_KEY, String(open)); } catch { /* Optional persistence. */ }
  }, [open, storage]);

  useEffect(() => {
    try { if (sectionId) storage?.setItem(GUEST_DEBUG_PANEL_SECTION_KEY, sectionId); } catch { /* Optional persistence. */ }
  }, [sectionId, storage]);

  const active = sections.find((s) => s.id === sectionId) ?? sections[0];
  const isolation = DEBUG_SURFACE_EVENT_ISOLATION;

  if (!open) {
    return (
      <div className='fixed right-2 top-2' style={{ zIndex: Z_DEBUG, touchAction: 'manipulation' }} {...isolation}>
        <button
          type='button'
          data-testid='guest-debug-tab'
          onClick={() => setOpen(true)}
          className='rounded px-2 py-1 font-mono text-xs font-bold text-black shadow-lg'
          style={{ backgroundColor: accentColor }}
        >
          Debug
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid='guest-debug-panel'
      className='fixed right-0 top-0 flex h-full w-[min(26rem,100vw)] flex-col bg-zinc-900/95 font-mono text-xs text-white shadow-2xl backdrop-blur'
      style={{ zIndex: Z_DEBUG, touchAction: 'manipulation' }}
      {...isolation}
    >
      <div className='flex items-center gap-1 border-b border-zinc-700 px-2 py-1'>
        {sections.map((section) => (
          <button
            key={section.id}
            type='button'
            data-testid={`guest-debug-section-${section.id}`}
            onClick={() => setSectionId(section.id)}
            className='rounded px-2 py-1 font-bold'
            style={
              section.id === active?.id
                ? { backgroundColor: accentColor, color: '#000' }
                : { color: '#a1a1aa' }
            }
          >
            {section.label}
          </button>
        ))}
        <span className='flex-1' />
        <button
          type='button'
          data-testid='guest-debug-close'
          aria-label='Close debug panel'
          onClick={() => setOpen(false)}
          className='rounded px-2 py-1 text-base leading-none text-zinc-400 hover:text-white'
        >
          &times;
        </button>
      </div>
      <div className='min-h-0 flex-1 overflow-y-auto p-3'>{active?.content ?? null}</div>
    </div>
  );
}
